(() => {
  'use strict';

  const DESKTOP_QUERY = '(min-width: 1024px)';

  class ProductMediaGrid extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.revealButton = this.querySelector('[data-media-reveal-button]');
      this.revealLabel = this.querySelector('[data-media-reveal-label]');
      this.revealStatus = this.querySelector('[data-media-reveal-status]');
      this.mediaItems = [...this.querySelectorAll('[data-split-media-index]')];
      this.cartBar = this.productInfo?.querySelector('cart-bar');
      this.purchaseFormWrapper = this.productInfo?.querySelector(
        '[data-product-purchase-rail] [data-form-wrapper]',
      );
      this.initialCount = Number.parseInt(this.dataset.mediaInitialCount || '4', 10);
      this.revealEnabled = this.dataset.mediaRevealEnabled === 'true';
      this.expanded = false;
      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.cartBarFrame = null;

      this.onRevealClick = () => this.setExpanded(!this.expanded, true);
      this.onViewportChange = () => {
        this.syncMediaVisibility();
        this.syncCartBarVisibility();
      };
      this.onScroll = () => {
        if (this.cartBarFrame) return;

        this.cartBarFrame = window.requestAnimationFrame(() => {
          this.cartBarFrame = null;
          this.syncCartBarVisibility();
        });
      };
      this.onOptionChange = (event) => {
        const eventTarget = event?.data?.event?.target;
        if (
          this.desktopQuery.matches &&
          !this.expanded &&
          eventTarget &&
          this.productInfo?.contains(eventTarget)
        ) {
          this.setExpanded(true, false);
        }
      };

      this.revealButton?.addEventListener('click', this.onRevealClick);
      document.addEventListener('theme:resize:width', this.onViewportChange);
      window.addEventListener('scroll', this.onScroll, { passive: true });

      if (window.subscribe && window.theme?.PUB_SUB_EVENTS?.optionValueSelectionChange) {
        this.unsubscribeOptionChange = window.subscribe(
          window.theme.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.onOptionChange,
        );
      }

      this.syncMediaVisibility();
      this.syncCartBarVisibility();
    }

    setExpanded(expanded, announce) {
      this.expanded = expanded;
      this.syncMediaVisibility();

      if (announce && this.revealStatus) {
        this.revealStatus.textContent = expanded
          ? this.dataset.showLessLabel
          : this.dataset.showMoreLabel;
      }
    }

    syncMediaVisibility() {
      const collapseMedia =
        this.desktopQuery.matches && this.revealEnabled && !this.expanded;

      this.mediaItems.forEach((item, index) => {
        const shouldHide = collapseMedia && index >= this.initialCount;
        item.toggleAttribute('data-split-media-hidden', shouldHide);

        if (shouldHide) {
          item.setAttribute('aria-hidden', 'true');
          item.setAttribute('inert', '');
        } else {
          item.removeAttribute('aria-hidden');
          item.removeAttribute('inert');
        }
      });

      this.toggleAttribute('data-media-expanded', this.expanded);

      if (this.revealButton) {
        const canReveal =
          this.desktopQuery.matches &&
          this.revealEnabled &&
          this.mediaItems.length > this.initialCount;

        this.revealButton.closest('.split-pdp__media-reveal')?.classList.toggle(
          'hidden',
          !canReveal,
        );
        this.revealButton.setAttribute('aria-expanded', String(this.expanded));

        if (this.revealLabel) {
          this.revealLabel.textContent = this.expanded
            ? this.dataset.showLessLabel
            : this.dataset.showMoreLabel;
        }
      }
    }

    syncCartBarVisibility() {
      if (!this.cartBar || !this.purchaseFormWrapper || this.desktopQuery.matches) {
        return;
      }

      const formIsAboveViewport =
        this.purchaseFormWrapper.getBoundingClientRect().bottom < 0;
      this.cartBar.classList.toggle('is-visible', formIsAboveViewport);
    }

    disconnectedCallback() {
      this.revealButton?.removeEventListener('click', this.onRevealClick);
      document.removeEventListener('theme:resize:width', this.onViewportChange);
      window.removeEventListener('scroll', this.onScroll);
      if (this.cartBarFrame) window.cancelAnimationFrame(this.cartBarFrame);
      this.unsubscribeOptionChange?.();
    }
  }

  class ProductVariantSku extends HTMLElement {
    connectedCallback() {
      this.valueElement = this.querySelector('[data-variant-sku]');
      this.productInfo = this.closest('product-info');
      this.hideWhenBlank = this.dataset.hideWhenBlank === 'true';

      this.onVariantChange = ({ data }) => {
        if (!data?.variant || data.sectionId !== this.productInfo?.dataset.section) {
          return;
        }

        this.updateSku(data.variant.sku || '');
      };

      if (window.subscribe && window.theme?.PUB_SUB_EVENTS?.variantChange) {
        this.unsubscribeVariantChange = window.subscribe(
          window.theme.PUB_SUB_EVENTS.variantChange,
          this.onVariantChange,
        );
      }
    }

    updateSku(sku) {
      if (this.valueElement) {
        this.valueElement.textContent = sku;
      }

      this.classList.toggle('hidden', this.hideWhenBlank && sku.trim() === '');
    }

    disconnectedCallback() {
      this.unsubscribeVariantChange?.();
    }
  }

  class InlineRelatedProducts extends HTMLElement {
    connectedCallback() {
      this.contentId = this.dataset.contentId;
      this.content = this.querySelector('[data-inline-related-content]');
      this.status = this.querySelector('[data-inline-related-status]');

      if (this.content?.querySelector('[data-product-block]')) {
        this.finishLoading();
        return;
      }

      this.loadRecommendations();
    }

    async loadRecommendations() {
      if (!this.dataset.url || this.loading) return;
      this.loading = true;

      try {
        const response = await fetch(this.dataset.url);
        if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

        const html = await response.text();
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const responseContent = parsedDocument.getElementById(this.contentId);

        if (!responseContent?.querySelector('[data-product-block]')) {
          this.hidden = true;
          this.setAttribute('aria-busy', 'false');
          return;
        }

        this.content.innerHTML = responseContent.innerHTML;
        this.hidden = false;
        this.finishLoading();
      } catch (error) {
        this.hidden = true;
        this.setAttribute('aria-busy', 'false');
        console.warn('Unable to load related products.', error);
      }
    }

    finishLoading() {
      this.setAttribute('aria-busy', 'false');
      this.classList.remove('hidden');
      this.querySelectorAll('[data-aos]').forEach((element) => {
        element.classList.add('aos-animate');
      });

      if (this.status) {
        this.status.textContent =
          this.querySelector('.split-pdp__recommendations-title')?.textContent || '';
      }
    }

  }

  if (!customElements.get('product-media-grid')) {
    customElements.define('product-media-grid', ProductMediaGrid);
  }

  if (!customElements.get('product-variant-sku')) {
    customElements.define('product-variant-sku', ProductVariantSku);
  }

  if (!customElements.get('inline-related-products')) {
    customElements.define('inline-related-products', InlineRelatedProducts);
  }

  const BULK_TIP_LINK_SELECTOR = '.split-pdp__bulk-tip-link';
  const BULK_TIP_EXTRA_OFFSET = 24;

  function getStickyHeaderOffset() {
    const stickyHeader =
      document.querySelector('[data-header-sticky]') ||
      document.querySelector('[data-site-header]');
    const announcement = document.querySelector('[data-announcement-bar], .announcement-bar-static');
    const headerHeight = stickyHeader?.getBoundingClientRect().height || 0;
    const announcementHeight =
      announcement && !document.body.classList.contains('has-scrolled')
        ? announcement.getBoundingClientRect().height
        : 0;

    return headerHeight + announcementHeight + BULK_TIP_EXTRA_OFFSET;
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest(BULK_TIP_LINK_SELECTOR);
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const href = link.getAttribute('href');
    if (!href || href.charAt(0) !== '#') return;

    const target = document.getElementById(href.slice(1));
    if (!target) return;

    event.preventDefault();

    const top = target.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset();
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

    if (history.pushState) {
      history.pushState(null, '', href);
    }
  });
})();
